import React, { useState } from 'react';
import styled from 'styled-components';
import SendIcon from '@mui/icons-material/Send';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

const QueryInput = ({ onSubmit, disabled, isProcessing, progress }) => {
  const [query, setQuery] = useState('');

  const exampleQueries = [
    'Show me sentiment distribution for fragrance theme',
    'Compare effectiveness ratings between male and female respondents',
    'What are the top 3 themes with highest negative sentiment?',
    'Show eco-friendly mentions by age group',
    'Analyze price perception across different regions',
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim() && !disabled && !isProcessing) {
      onSubmit(query.trim());
    }
  };

  const handleExampleClick = (example) => {
    if (!disabled && !isProcessing) {
      setQuery(example);
    }
  };

  return (
    <Container>
      <Form onSubmit={handleSubmit}>
        <TextArea
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Ask a question about your survey data... (e.g., 'Show me sentiment for fragrance theme')"
          disabled={disabled || isProcessing}
          rows={4}
        />
        
        <SubmitButton
          type="submit"
          disabled={disabled || isProcessing || !query.trim()}
        >
          {isProcessing ? (
            <>
              <AutoAwesomeIcon style={{ marginRight: '0.5rem', animation: 'spin 2s linear infinite' }} />
              Processing...
            </>
          ) : (
            <>
              <SendIcon style={{ marginRight: '0.5rem' }} />
              Ask Question
            </>
          )}
        </SubmitButton>
      </Form>

      {isProcessing && progress && (
        <ProgressContainer>
          <ProgressBar>
            <ProgressFill percentage={progress.percentage} />
          </ProgressBar>
          <ProgressText>
            <strong>{progress.stage}</strong>: {progress.message}
          </ProgressText>
        </ProgressContainer>
      )}

      {!isProcessing && (
        <ExamplesSection>
          <ExamplesTitle>Example queries:</ExamplesTitle>
          <ExamplesList>
            {exampleQueries.map((example, index) => (
              <ExampleChip
                key={index}
                onClick={() => handleExampleClick(example)}
                disabled={disabled}
              >
                {example}
              </ExampleChip>
            ))}
          </ExamplesList>
        </ExamplesSection>
      )}
    </Container>
  );
};

export default QueryInput;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 1rem;
  font-size: 1rem;
  font-family: inherit;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  resize: vertical;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #008080;
    box-shadow: 0 0 0 3px rgba(0, 128, 128, 0.1);
  }

  &:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
  }

  &::placeholder {
    color: #999;
  }
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.875rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: white;
  background: linear-gradient(135deg, #008080 0%, #006666 100%);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 128, 128, 0.3);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const ProgressContainer = styled.div`
  background: #f0f9f9;
  padding: 1rem;
  border-radius: 8px;
  border-left: 4px solid #008080;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.5rem;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${(props) => props.percentage}%;
  background: linear-gradient(90deg, #008080 0%, #00a0a0 100%);
  transition: width 0.3s ease;
`;

const ProgressText = styled.div`
  font-size: 0.9rem;
  color: #333;

  strong {
    color: #008080;
  }
`;

const ExamplesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ExamplesTitle = styled.div`
  font-size: 0.9rem;
  color: #666;
  font-weight: 600;
`;

const ExamplesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const ExampleChip = styled.button`
  padding: 0.5rem 0.875rem;
  font-size: 0.85rem;
  color: #008080;
  background: white;
  border: 1px solid #008080;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #008080;
    color: white;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
